import React from 'react';
import ReactDOM from 'react-dom';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, Form, FormError, FormField, FormSchemaEntry, FormType } from '@gik/ui';
import { translationKeys } from './i18n/en';
import { useTranslation } from 'react-i18next';
import { IUserSignUpResponse, userSignUp } from '@gik/api/users/auth';
import i18n from '@gik/i18n';
import { useAuthStore } from '@gik/auth/store/AuthStore';

const blockName = 'signup-form';
export const signupFormId = 'signupForm';
export interface IUserSignupFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordRepeat: string;
  firstNamePlaceholder?: string;
  lastNamePlaceholder?: string;
  subscribeToNewsletter?: boolean;
}

export interface ISignupFormProps {
  onLogin?(): void;

  onChange?: (values: object) => void;
  initialValues?: Partial<IUserSignupFormValues>;
  buttonsPortal?: () => HTMLElement;
  onSuccess?: (doneResponse: IUserSignUpResponse) => void;
  onAttemptStart?: () => void;
  onAttemptEnd?: () => void;
  firstNamePlaceholder?: string;
  lastNamePlaceholder?: string;
  hideButtons?: boolean;
  hideEmailField?: boolean;
  trackingId?: string;
  buttonsOverride?: (valid: boolean, submitting: boolean, values: object) => React.ReactNode;
  onSubmitOverride?: (values: IUserSignupFormValues) => Promise<void>;
  className?: string;
}

export const subscribeToNewsletterInitialValue = true;

export default React.forwardRef<FormType, ISignupFormProps>(function SignupForm(
  {
    initialValues,
    onChange,
    buttonsPortal,
    onSuccess,
    onLogin,
    onAttemptStart,
    onAttemptEnd,
    hideButtons = false,
    hideEmailField = false,
    trackingId,
    buttonsOverride,
    onSubmitOverride,
    className,
  },
  ref
) {
  const bem = bemBlock(blockName);
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const formRef = React.useRef<FormType>();
  React.useImperativeHandle(ref, () => formRef.current);

  const setSignupFormValues = useAuthStore(state => state.setSignupFormValues);

  React.useEffect(() => {
    setSignupFormValues(initialValues);
  }, []);

  function handleChange(formValues) {
    onChange?.(formValues);
    setSignupFormValues(formValues);
  }

  // TODO: remove duplication between signup form schema and user settings schema
  const schema: FormSchemaEntry[] = [
    {
      placeholder: 'First name',
      name: 'firstName',
      type: 'text',
      required: true,
      minLength: 2,
      maxLength: 255,
      props: { autoComplete: 'given-name' },
    },
    {
      placeholder: 'Last name',
      name: 'lastName',
      type: 'text',
      required: true,
      minLength: 2,
      maxLength: 255,
      props: { autoComplete: 'family-name' },
    },
    {
      placeholder: 'Email address',
      name: 'email',
      type: 'email',
      required: true,
      minLength: 2,
      maxLength: 255,
    },
    {
      placeholder: 'Enter Password',
      name: 'password',
      type: 'password',
      required: true,
      minLength: 8,
      maxLength: 255,
      props: {
        autoComplete: 'new-password',
      },
    },
    {
      placeholder: 'Re-Enter Password',
      name: 'passwordRepeat',
      type: 'password',
      required: true,
      mustEqual: 'password',
      minLength: 8,
      maxLength: 255,
      props: {
        autoComplete: 'new-password',
      },
    },
    {
      name: 'subscribeToNewsletter',
      type: 'checkbox',
      default: subscribeToNewsletterInitialValue,
      props: {
        label: i18n.t(translationKeys.subscribeToNewsletter),
      },
    },
  ];

  async function handleSubmit(values: IUserSignupFormValues) {
    onAttemptStart?.();
    setErrorMessage(null);
    try {
      const response = await userSignUp({
        emailAddress: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
        subscribeToNewsletter: values.subscribeToNewsletter,
      });

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (err) {
      setErrorMessage(err.message);
    }
    onAttemptEnd?.();
  }

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmitOverride || handleSubmit}
      onChange={handleChange}
      schema={schema}
      ref={formRef}
      vertical
      id={signupFormId}
      className={bem(null, null, className)}
      trackingId={trackingId}
      render={({ valid, submitting, values }) => {
        const buttons = buttonsOverride ? (
          buttonsOverride(valid, submitting, values)
        ) : (
          <div className={bem('buttons')}>
            <Button
              className={bem('btnSubmit')}
              variant="primary"
              type="submit"
              size="lg"
              loading={submitting}
              disabled={!valid}
              form={signupFormId}
              fullWidth
            >
              {t(translationKeys.formSubmitButton)}
            </Button>
            <p>
              Already have an account?{' '}
              <Button variant="primary-link" onClick={() => onLogin()}>
                Login
              </Button>
            </p>
          </div>
        );

        return (
          <>
            {errorMessage && <FormError message={errorMessage} centered />}
            <section className={bem('fullNameFields')}>
              <FormField name="firstName" />
              <FormField name="lastName" />
            </section>
            {!hideEmailField && <FormField name="email" />}
            <FormField name="password" />
            <FormField name="passwordRepeat" />
            <FormField name="subscribeToNewsletter" />

            <p className={bem('tos')}>
              By creating an account you are agreeing to the{' '}
              <Button variant={'primary-link'} externalLink href="/terms-service">
                Terms & Conditions
              </Button>
            </p>

            {buttonsPortal?.() && !hideButtons && ReactDOM.createPortal(buttons, buttonsPortal())}
            {!buttonsPortal?.() && !hideButtons && buttons}
          </>
        );
      }}
    />
  );
});
